/**
 * K31: paid fast travel (docs/DECISIONS_2026-10-11.md "Open world + paid warps"). Stations: every open zone's plaza
 * (a horse station when the zone has a carriage master, a sect gate for sect-*), and both ends of each seam gate.
 * Walking stays seamless; a warp between discovered stations costs silver by distance. Data only.
 */
import { ENABLED_ZONES } from './enabledZones';
import { PLACES, REGIONS, SEAM_GATES, type RegionId } from './regions';
import { STATIONS } from './carriage';
import { ZONES, type ZoneId } from './zones';

export type StationKind = 'horse' | 'gate' | 'sect' | 'plaza';

export interface FastStation {
  /** `z:<zone>` or `g:<gate>:<a|b>`. */
  readonly id: string;
  readonly name: string;
  readonly kind: StationKind;
  /** The zone a warp lands in (null for a gate end: the region's landing zone, resolved by the system). */
  readonly zone: ZoneId | null;
  readonly region: RegionId | null;
  /** Region metres, when placed. */
  readonly x: number | null;
  readonly y: number | null;
}

export const FAST_TRAVEL = {
  /** Silver: flat part + per metre. */
  base: 20,
  perMetre: 0.1,
  /** Flat fare when either end is not in regions.ts. */
  flat: 60,
  /** Seconds between warps. */
  cooldown: 120,
  /** Walking pace used to judge whether a warp is worth routing through (m/s), and the saving needed (s). */
  walkSpeed: 6,
  navSaving: 120,
  /** Walking seconds assumed between zones that are not on the region map. */
  flatWalk: 240,
} as const;

const zoneStation = (zone: ZoneId): FastStation => {
  const pl = PLACES.find((p) => p.zone === zone);
  const kind: StationKind = zone.startsWith('sect-') ? 'sect' : STATIONS[zone] ? 'horse' : 'plaza';
  const suffix = kind === 'sect' ? 'ประตูสำนัก' : kind === 'horse' ? 'ท่าม้า' : 'ลานกลาง';
  return {
    id: `z:${zone}`,
    name: `${ZONES[zone].name} ${suffix}`,
    kind,
    zone,
    region: pl?.region ?? null,
    x: pl ? pl.rect.x + pl.rect.w / 2 : null,
    y: pl ? pl.rect.y + pl.rect.h / 2 : null,
  };
};

export const FAST_STATIONS: readonly FastStation[] = [
  ...ENABLED_ZONES.map(zoneStation),
  ...SEAM_GATES.flatMap((g) =>
    (['a', 'b'] as const).map(
      (end): FastStation => ({
        id: `g:${g.id}:${end}`,
        name: `${g.name} (${REGIONS[g[end].region].name})`,
        kind: 'gate',
        zone: null,
        region: g[end].region,
        x: g[end].x,
        y: g[end].y,
      }),
    ),
  ),
];

export const stationById = (id: string): FastStation | undefined => FAST_STATIONS.find((s) => s.id === id);
